/**
 * SqlStudioPanel — "natural language → SQL, editable, executable."
 *
 * Two execution modes, picked via the toggle at the top of the panel:
 *
 *   ▸ OTBI Logical SQL (default)
 *       The query is generated for an Oracle Business Intelligence subject
 *       area and executed via OTBI's SOAP endpoint. Read-only, semantic-
 *       model–driven, no JOINs.
 *       Endpoints:  POST /api/copilot/compose-sql, /run-sql
 *
 *   ▸ Oracle SQL (BIP)
 *       The query is generated as native Oracle SQL against the Fusion HCM
 *       physical schema (PER_*, HR_*, PAY_*, ANC_*, CMP_*). It is executed
 *       by submitting it to the "AI SQL Runner" BI Publisher data model
 *       installed on the customer tenant (see assets/bip-templates/).
 *       Endpoints:  POST /api/copilot/compose-oracle-sql (optional `revision` body; response may include `revisionMeta` when revising), /run-oracle-sql,
 *                   POST /api/copilot/run-oracle-sql/export  (blob — only after user picks a format),
 *                   GET  /api/copilot/oracle-sql-runner/probe
 *                   GET  /api/copilot/oracle-schema, /oracle-schema/:table — browse curated digest
 *                   POST /api/copilot/oracle-schema/validate-columns — { sql } → schema lint warnings
 *
 * Both modes share the same workflow: type a question → generate → optionally
 * edit the SQL → run / re-run. OTBI can download a client-built CSV from the
 * result bar. Oracle (BIP) loads a **preview** via a configured BIP report. Excel / CSV / PDF
 * via BIP call the export API; when the layout advertises xml only, the server fetches XML and
 * converts to the requested format. SAAS Tool default path is unchanged.
 * Probe results are cached; use **Re-check installation** to refresh.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2,
  AlertCircle,
  Download,
  Clock,
  Play,
  Copy,
  Check,
  Save,
  Sparkles,
  RotateCcw,
  Terminal,
  ExternalLink,
  ShieldAlert,
  FileSpreadsheet,
  FileText,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import { getApiBase } from '../apiBase';
import { ORACLE_SQL_STARTERS } from './queryHintCatalog';
import { sqlLooksLikeSecurityAccessMap } from './UserSecurityAccessMap';
import { SECURITY_ACCESS_MAP_ROW_LIMIT } from './securityAccessMapApi';
import CollapsibleStrip from './CollapsibleStrip';
import SqlStudioToolbar from './SqlStudioToolbar';
import QueryHintChips from './QueryHintChips';
import { deriveOracleRunHdlFlags } from './hdlSqlStudioHelpers';
import { saveSqlQuery, updateSqlQuery, formatDefaultQueryName, clampSqlStudioRowLimit, loadSqlStudioRowLimit, saveSqlStudioRowLimit, SQL_STUDIO_OTBI_ROW_LIMIT_MAX, SQL_STUDIO_ORACLE_ROW_LIMIT_MAX } from './sqlStudioApi';
import ConnectionRequiredNotice from '../ConnectionRequiredNotice';

function questionLooksLikeSecurityAccessMap(question) {
  if (!question || typeof question !== 'string') return false;
  const t = question.toLowerCase();
  return (
    /\b(access map|security access map|user access map|data security map)\b/.test(t) ||
    (/\b(functional|duty)\s+role/.test(t) && /\buser/.test(t)) ||
    /\bwhat can\b.*\buser\b.*\bsee\b/.test(t)
  );
}

function effectiveRowLimitForSql(modeId, sql, question, rowLimit) {
  if (modeId !== 'oracle') return rowLimit;
  if (sqlLooksLikeSecurityAccessMap(sql) || questionLooksLikeSecurityAccessMap(question)) {
    return Math.max(rowLimit, SECURITY_ACCESS_MAP_ROW_LIMIT);
  }
  return rowLimit;
}

const ORACLE_PROBE_CACHE_KEY = 'aiTool.sqlStudio.oracleRunnerProbe.v6';

const BIP_EXPORT_TOOLTIP =
  'Configure AI_SQL_RUNNER_REPORT_PATH (or connection aiSqlRunnerReportPath) to export via BI Publisher.';

const BIP_XLSX_FORMAT_KEYS = new Set([
  'xlsx',
  'excel2007',
  'spreadsheet',
  'msexcel',
  'excel',
  'xls',
  'excel2000',
]);

function oracleBipFormatsList(probe, runResult) {
  const fromProbe = probe?.bipSupportedFormats;
  if (Array.isArray(fromProbe) && fromProbe.length) {
    return fromProbe.map((s) => String(s).toLowerCase().trim()).filter(Boolean);
  }
  const fromRun = runResult?.runner?.bipSupportedFormats;
  if (Array.isArray(fromRun) && fromRun.length) {
    return fromRun.map((s) => String(s).toLowerCase().trim()).filter(Boolean);
  }
  return [];
}

/** Drive Sql Studio export buttons from probe / last Preview runner metadata. */
function deriveOracleBipExportCaps(probe, runResult) {
  const formats = oracleBipFormatsList(probe, runResult);
  const sup = new Set(formats);
  const onlyXml = formats.length > 0 && formats.every((f) => f === 'xml');
  const hasCsv = sup.has('csv') || sup.has('text');
  const hasXlsx = [...BIP_XLSX_FORMAT_KEYS].some((k) => sup.has(k));
  const hasPdf = sup.has('pdf');
  const hasXml = sup.has('xml');
  const formatsUnknown = formats.length === 0;
  const bipExportVia =
    probe?.bipExportVia ||
    (formats.length ? `Export via BIP: ${formats.join(', ')} only` : null);

  return {
    formats,
    onlyXml,
    hasCsv,
    hasXlsx,
    hasPdf,
    hasXml,
    formatsUnknown,
    bipExportVia,
    bipXlsxEnabled: formatsUnknown || hasXlsx || onlyXml,
    bipPdfEnabled: formatsUnknown || hasPdf || onlyXml,
    bipCsvBipEnabled: formatsUnknown || hasCsv || onlyXml,
    bipXmlEnabled: onlyXml || hasXml,
    usePreviewCsvPrimary: !hasCsv && !onlyXml,
  };
}

const BIP_EXPORT_XLSX_TOOLTIP =
  'Download Excel (.xlsx) via BIP. When the layout is xml-only, the server converts BIP XML to Excel.';

const BIP_EXPORT_PDF_TOOLTIP =
  'Download PDF via BIP. When the layout is xml-only, the server converts BIP XML to a simple table PDF.';

const BIP_EXPORT_CSV_TOOLTIP =
  'Download CSV via BIP (full report output). When the layout is xml-only, the server converts BIP XML to CSV.';

function oracleRunnerPathConfigured(probe) {
  if (!probe) return false;
  if (probe.reportPathConfigured === true) return true;
  if (probe.reportPathConfigured === false) return false;
  if (probe.reportPathSource === 'saas_tool_default' && probe.reportPath) return true;
  return !!(probe.reportPath && probe.error?.kind !== 'not_configured');
}

function formatReportPathSource(source) {
  if (!source) return '';
  if (source === 'saas_tool_default') return 'SAAS Tool default';
  if (source === 'env') return 'server .env';
  if (source === 'connection') return 'connection override';
  if (source === 'override') return 'request override';
  return source;
}

function loadOracleProbeCache(connectionKey) {
  if (!connectionKey || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(ORACLE_PROBE_CACHE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (o.connectionKey !== connectionKey) return null;
    const maxAge = 24 * 60 * 60 * 1000;
    if (Date.now() - (o.ts || 0) > maxAge) return null;
    if (!o.payload || typeof o.payload.installed !== 'boolean') return null;
    // Invalidate client cache from before `/shared/Custom/...` → `/Custom/...` normalization.
    if (o.payload.bipProbeApiVersion != null && o.payload.bipProbeApiVersion < 6) return null;
    return o.payload;
  } catch {
    return null;
  }
}

function saveOracleProbeCache(connectionKey, payload) {
  if (!connectionKey || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(
      ORACLE_PROBE_CACHE_KEY,
      JSON.stringify({ connectionKey, ts: Date.now(), payload })
    );
  } catch (_) {
    /* quota / private mode */
  }
}

const RUNNER_PATH_ENV_HINT =
  'AI_SQL_RUNNER_REPORT_PATH (or BIP_SQL_RUNNER_REPORT_PATH / ORACLE_BI_SQL_RUNNER_REPORT)';

const API = axios.create({
  baseURL: getApiBase(),
  withCredentials: true,
  timeout: 240000,
});

/** API may return `error` as a string or `{ kind, message, oraCode }` — never pass objects to React children. */
function formatApiError(value, fallback = 'Request failed') {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const msg = value.message || value.error || value.msg;
    const code = value.oraCode || value.code;
    if (msg && code) return `${code}: ${msg}`;
    if (msg) return String(msg);
    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }
  return String(value);
}

/** Prefer composed SQL; on OTBI partial failure fall back to the last attempt that produced SQL. */
function resolveComposedSql(data) {
  if (data?.sql) return data.sql;
  const attempts = Array.isArray(data?.attempts) ? data.attempts : [];
  for (let i = attempts.length - 1; i >= 0; i -= 1) {
    const sql = attempts[i]?.sql;
    if (sql && String(sql).trim()) return sql;
  }
  return '';
}

/** REST 404 on every format — report missing; not the same as SQL returning zero rows. */
function bipRestReportNotFound(runner, errorKind) {
  if (errorKind === 'bip_report_not_found' || errorKind === 'not_installed') return true;
  const trace = runner?.restTrace;
  if (!Array.isArray(trace) || !trace.length) return false;
  if (trace.some((t) => t.hadReportData === true)) return false;
  const statuses = trace.map((t) => t.status).filter((s) => typeof s === 'number');
  return statuses.length > 0 && statuses.every((s) => s === 404);
}

function bipNotFoundPathLabel(runner) {
  const p =
    runner?.reportPath ||
    runner?.restReportPathUsed ||
    (Array.isArray(runner?.restPathsTried) && runner.restPathsTried[0]) ||
    '';
  return p || '(path not set — configure AI_SQL_RUNNER_REPORT_PATH)';
}

function normalizeOracleRunPayload(data) {
  if (!data || typeof data !== 'object') return data;
  if (data.ok !== false || data.error == null) return data;
  if (typeof data.error === 'string') {
    return { ...data, errorKind: data.errorKind || null };
  }
  if (typeof data.error === 'object') {
    const kind = data.error.kind || null;
    const err = data.error;
    const text = `${err.oraCode ? `${err.oraCode}: ` : ''}${formatApiError(err, 'execution failed')}`;
    return { ...data, error: text, errorKind: kind };
  }
  return data;
}

// ─── Mode definitions ──────────────────────────────────────────────────────
//
// Each mode owns its endpoints, labels, and starter prompts. Centralising
// these here keeps the render code branch-free.
const MODES = {
  otbi: {
    id: 'otbi',
    label: 'OTBI Logical SQL',
    short: 'OTBI',
    description: 'OTBI semantic model SQL',
    composeUrl: '/api/copilot/compose-sql',
    runUrl: '/api/copilot/run-sql',
    sqlLanguageLabel: 'Logical SQL',
    accent: 'indigo',
    starters: [
      'Show employees enrolled in absence plan "US Paid Sick Time Annual"',
      'Average salary by job',
      'Top 10 employees by salary',
      'Workforce headcount by department',
      'Absences taken in the last 30 days, grouped by type',
    ],
  },
  oracle: {
    id: 'oracle',
    label: 'Oracle SQL (BIP)',
    short: 'Oracle SQL',
    description: 'Fusion physical schema (PER_*, HR_*, PAY_*)',
    composeUrl: '/api/copilot/compose-oracle-sql',
    runUrl: '/api/copilot/run-oracle-sql',
    sqlLanguageLabel: 'Oracle SQL',
    accent: 'violet',
    starters: ORACLE_SQL_STARTERS,
  },
};

function fmtCount(n) {
  return Number.isFinite(n) ? n.toLocaleString() : '0';
}

/** Oracle SQL Studio — rate generated SQL after Preview (visible even without learningOutcomeId). */
function OracleSqlFeedbackBar({
  needsPreview,
  learningEnabled,
  userVerdict,
  verdictBusy,
  onPositive,
  onNegative,
  className = '',
}) {
  if (needsPreview) {
    return (
      <div
        className={`rounded-lg border border-violet-200 bg-violet-50/90 px-3 py-2.5 text-[11px] text-violet-950 ${className}`}
      >
        <span className="font-semibold text-violet-900">Rate this SQL</span>
        <span className="mt-0.5 block text-violet-800/95">
          Run <strong>Preview</strong> first, then mark the query good or bad so similar questions improve over time.
        </span>
      </div>
    );
  }

  if (userVerdict) {
    return (
      <div
        className={`flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/90 px-3 py-2 text-[11px] text-emerald-900 ${className}`}
      >
        <Check className="h-4 w-4 shrink-0 text-emerald-600" />
        <span>
          Feedback saved as <strong>{userVerdict}</strong> — thank you.
        </span>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-wrap items-center gap-2 rounded-lg border border-violet-200 bg-violet-50/90 px-3 py-2.5 ${className}`}
    >
      <span className="text-[11px] font-semibold text-violet-900">Was this SQL helpful?</span>
      {!learningEnabled ? (
        <span className="text-[10px] text-violet-800/90">
          Requires MongoDB — set <code className="rounded bg-violet-100 px-0.5 font-mono">MONGODB_URI</code> in{' '}
          <code className="rounded bg-violet-100 px-0.5 font-mono">.env</code> and restart the server.
        </span>
      ) : (
        <>
          <span className="text-[10px] text-violet-800/85">Thumbs up teaches the copilot to reuse these patterns.</span>
          <div className="ml-auto flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              title="Good SQL"
              disabled={verdictBusy || !learningEnabled}
              onClick={onPositive}
              className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-white px-2.5 py-1 text-[11px] font-medium text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
            >
              {verdictBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ThumbsUp className="h-3.5 w-3.5" />}
              Good
            </button>
            <button
              type="button"
              title="Not helpful"
              disabled={verdictBusy || !learningEnabled}
              onClick={onNegative}
              className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white px-2.5 py-1 text-[11px] font-medium text-rose-800 hover:bg-rose-50 disabled:opacity-50"
            >
              <ThumbsDown className="h-3.5 w-3.5" />
              Not helpful
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function downloadCsv(filename, columns, rows) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  // rows here are array-of-objects keyed by column label.
  const text = [
    columns.map(esc).join(','),
    ...rows.map((r) => columns.map((c) => esc(formatCellValue(r[c]))).join(',')),
  ].join('\n');
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Render a cell, normalizing ISO date/datetime strings to YYYY-MM-DD. */
function formatCellValue(v) {
  if (v == null) return '';
  const s = String(v);
  // 2026-01-05T00:00:00.000+00:00 / 2026-01-05 00:00:00 / 2026-01-05 → 2026-01-05
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ]\d{2}:\d{2}.*)?$/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  return s;
}

function ResultTable({ columns, rows, maxRows = 200 }) {
  const cols =
    Array.isArray(columns) && columns.length
      ? columns.map((c) => (typeof c === 'string' ? c : c.name)).filter(Boolean)
      : Object.keys(rows[0] || {});
  const view = rows.slice(0, maxRows);

  // Synced top + bottom horizontal scrollbars.
  const topRef = useRef(null);
  const bodyRef = useRef(null);
  const tableRef = useRef(null);
  const [scrollWidth, setScrollWidth] = useState(0);

  useEffect(() => {
    const measure = () => {
      if (tableRef.current) setScrollWidth(tableRef.current.scrollWidth);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [cols.length, view.length]);

  const syncFromTop = () => {
    if (bodyRef.current && topRef.current) bodyRef.current.scrollLeft = topRef.current.scrollLeft;
  };
  const syncFromBody = () => {
    if (bodyRef.current && topRef.current) topRef.current.scrollLeft = bodyRef.current.scrollLeft;
  };

  return (
    <div className="rounded-md border border-slate-300">
      {/* Top horizontal scrollbar, synced with the sheet below. */}
      <div
        ref={topRef}
        onScroll={syncFromTop}
        className="overflow-x-auto overflow-y-hidden rounded-t-md bg-slate-50"
      >
        <div style={{ width: scrollWidth || '100%', height: 1 }} />
      </div>
      {/* Excel-style sheet: gridlines, sticky header + row-number gutter, both-axis scroll. */}
      <div ref={bodyRef} onScroll={syncFromBody} className="max-h-[32rem] overflow-auto">
        <table ref={tableRef} className="min-w-full border-collapse text-xs tabular-nums">
          <thead className="sticky top-0 z-20 text-slate-700">
            <tr>
              <th className="sticky left-0 z-30 border border-slate-300 bg-slate-200 px-2 py-1.5 text-right font-semibold text-slate-500">
                #
              </th>
              {cols.map((c, i) => (
                <th
                  key={i}
                  className="border border-slate-300 bg-slate-100 px-2.5 py-1.5 text-left font-semibold whitespace-nowrap"
                  title={c}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.map((row, r) => (
              <tr key={r} className={r % 2 ? 'bg-slate-50/60' : 'bg-white'}>
                <td className="sticky left-0 z-10 border border-slate-300 bg-slate-100 px-2 py-1 text-right font-medium text-slate-400">
                  {r + 1}
                </td>
                {cols.map((c, i) => {
                  const display = formatCellValue(row[c]);
                  return (
                    <td
                      key={i}
                      className="border border-slate-200 px-2.5 py-1 align-top text-slate-700"
                      title={display}
                    >
                      <div className="max-w-[24rem] truncate whitespace-nowrap">{display}</div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > maxRows && (
        <div className="border-t border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] text-slate-500">
          Showing first {maxRows.toLocaleString()} of {fmtCount(rows.length)} rows.
        </div>
      )}
    </div>
  );
}

/**
 * Shown in Oracle mode when the BIP SQL runner report is missing or misconfigured.
 */
function AiSqlRunnerSetupCard({ probe, onRetryProbe, retrying }) {
  const error = probe?.error || {};
  const path = probe?.reportPath;
  const pathSource = formatReportPathSource(probe?.reportPathSource || probe?.runner?.reportPathSource);
  const pathLabel = path
    ? `${path}${pathSource ? ` (${pathSource})` : ''}`
    : `(not configured — set ${RUNNER_PATH_ENV_HINT} or install SAAS Tool)`;
  const kind = error.kind || 'unknown';
  const failures = Array.isArray(probe?.runner?.failures) ? probe.runner.failures : [];
  const headline =
    kind === 'not_configured'
      ? 'AI SQL Runner BIP report path is not configured'
      : kind === 'permission'
      ? 'Integration user needs catalog access (BI Author / correct password)'
      : kind === 'missing_parameter'
      ? 'Report found but has no SQL bind parameter (expected query1 or p_sql)'
      : 'BIP SQL runner report not found or not runnable';
  return (
    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
      <div className="flex items-start gap-2.5">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-amber-900">{headline}</h3>
          {probe?.installed === false && probe?.bipProbeApiVersion != null && probe.bipProbeApiVersion < 3 && (
            <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] text-red-900">
              The browser is hitting an <strong>older API build</strong> (missing <code className="font-mono">bipProbeApiVersion</code>).
              From your <strong>AI Tool</strong> project folder run{' '}
              <code className="font-mono">git pull origin main</code>, stop Node on port <strong>3010</strong>, then{' '}
              <code className="font-mono">node server.js</code> again. Hard-refresh this page (Ctrl+Shift+R).
            </p>
          )}
          {kind === 'permission' ? (
            <p className="mt-1 text-xs text-amber-800/80">
              BIP returned <strong>401/403</strong> or an authorization fault. In <strong>Connections</strong>, use the
              same username/password that can open Analytics → Catalog and run the report. Grant{' '}
              <strong>BI Author</strong> (or equivalent) to that user, and confirm the password in Security Console
              matches what you saved here. Wrong credentials often look like “report not found.”
            </p>
          ) : kind === 'not_configured' ? (
            <p className="mt-1 text-xs text-amber-800/80">
              For SAAS Tool tenants, install the catalog bundle under{' '}
              <code className="font-mono">/Custom/SAAS Tool</code> — the server defaults to{' '}
              <code className="font-mono">/Custom/SAAS Tool/V1/SAAS Tool Report.xdo</code> when no path is set.
              Or set <code className="rounded bg-amber-100 px-1 font-mono text-[11px]">{RUNNER_PATH_ENV_HINT}</code>{' '}
              to override. SQL <strong>generation</strong> still works — <strong>Preview</strong> and BIP export need
              a working report.
            </p>
          ) : (
            <p className="mt-1 text-xs text-amber-800/80">
              Preview runs against the configured <strong>AI SQL Runner</strong> BIP report at{' '}
              <code className="rounded bg-amber-100 px-1 font-mono text-[11px]">{pathLabel}</code>.
              SQL <strong>generation</strong> still works without BIP — only <strong>Preview</strong> and BIP export
              need a working report.
            </p>
          )}
          <p className="mt-2 text-[11px] text-amber-800">
            Report path: <code className="font-mono">{RUNNER_PATH_ENV_HINT}</code> or per-connection{' '}
            <code className="font-mono">aiSqlRunnerReportPath</code>. Parameter:{' '}
            <code className="font-mono">BIP_SQL_RUNNER_QUERY_PARAM</code>; encoding:{' '}
            <code className="font-mono">BIP_SQL_RUNNER_SQL_ENCODING</code> (<code className="font-mono">base64</code> or{' '}
            <code className="font-mono">plain</code>).
          </p>
          <p className="mt-2 text-[11px] text-amber-700">
            Optional lexical template (raw SQL in <code className="font-mono">p_sql</code>): see{' '}
            <code className="rounded bg-amber-100 px-1 font-mono">assets/bip-templates/AI_SQL_Runner/README.md</code>.
          </p>

          {error.message && (
            <details className="mt-3 rounded-md bg-amber-100/60 px-2 py-1.5">
              <summary className="cursor-pointer text-[11px] font-medium text-amber-900">
                BIP error from probe (path: {pathLabel})
              </summary>
              <pre className="mt-1 overflow-auto whitespace-pre-wrap text-[10px] text-amber-800">
                {error.message}
              </pre>
            </details>
          )}

          {failures.length > 0 && (
            <details className="mt-2 rounded-md bg-amber-100/40 px-2 py-1.5">
              <summary className="cursor-pointer text-[11px] font-medium text-amber-900">
                Paths tried ({failures.length})
              </summary>
              <ul className="mt-1 list-inside list-disc space-y-1 text-[10px] text-amber-800">
                {failures.map((f, i) => (
                  <li key={i}>
                    <span className="font-mono">{f.reportPath || '?'}</span>
                    {f.kind ? ` — ${f.kind}` : ''}
                    {f.message ? `: ${String(f.message).slice(0, 160)}` : ''}
                  </li>
                ))}
              </ul>
            </details>
          )}

          <button
            type="button"
            onClick={onRetryProbe}
            disabled={retrying}
            className="mt-3 inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-60"
          >
            {retrying ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
            Re-check installation
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SqlStudioPanel({
  title = 'SQL Studio',
  subtitle,
  initialQuestion = null,
  onConsumeInitial,
  /** Navigate to User Lookup (optional username to focus in access map combobox). */
  onOpenUserLookup,
  hcmConnection = null,
  onOpenConnections,
  /** Stable key for the active HCM tenant (e.g. `db:ObjectId` or `env:https://host`). Used to cache probe results in localStorage. */
  connectionKey = '',
}) {
  const [modeId, setModeId] = useState('otbi');
  const mode = MODES[modeId];

  const [question, setQuestion] = useState('');
  const [generating, setGenerating] = useState(false);
  const [executing, setExecuting] = useState(false);
  /** Oracle BIP export: which format is currently generating a file (`csv` | `xlsx` | `pdf`). */
  const [exportingFormat, setExportingFormat] = useState(null);
  const [error, setError] = useState(null);

  // Most recent composition result. Shape varies by mode:
  //   OTBI:   { ok, sql, saName, saRationale, rationale, attempts, rows, columns, elapsedMs }
  //   Oracle: { ok, sql, rationale, rowLimit, elapsedMs }
  const [composed, setComposed] = useState(null);

  // Editable SQL textarea (starts as composed.sql, user can edit and re-run).
  const [editorSql, setEditorSql] = useState('');
  /** Bumps when a library recipe loads so the SQL editor strip auto-expands. */
  const [editorExpandSignal, setEditorExpandSignal] = useState(0);
  // Most recent execution result (normalised across modes).
  const [runResult, setRunResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [savedToLibrary, setSavedToLibrary] = useState(false);
  /** Bumps SqlStudioToolbar saved-query dropdown after editor Save. */
  const [savedQueriesRefreshKey, setSavedQueriesRefreshKey] = useState(0);
  /** Personal saved query currently loaded — Save updates this entry instead of creating a duplicate. */
  const [loadedSavedQuery, setLoadedSavedQuery] = useState(null);
  const [rowLimit, setRowLimit] = useState(loadSqlStudioRowLimit);
  /** Oracle: follow-up for "Refine with AI" (errors from Preview are sent automatically). */
  const [revisionNote, setRevisionNote] = useState('');
  /** After "Revise SQL with AI": summary from API (`revisionMeta`) so the user sees what changed. */
  const [refineMeta, setRefineMeta] = useState(null);

  // Oracle-mode-only: whether the AI SQL Runner BIP report is installed.
  const [oracleProbe, setOracleProbe] = useState(null); // { installed, reportPath, error }
  const [probing, setProbing] = useState(false);
  /** Oracle: model asked clarifying questions before generating SQL. */
  const [clarificationRound, setClarificationRound] = useState(null);
  const [clarificationAnswers, setClarificationAnswers] = useState('');

  /** Oracle learning: links compose → preview → user verdict for pattern boosts. */
  const [learningOutcomeId, setLearningOutcomeId] = useState(null);
  const [learningEnabled, setLearningEnabled] = useState(true);
  const [userVerdict, setUserVerdict] = useState(null);
  const [verdictBusy, setVerdictBusy] = useState(false);
  // Track which initialQuestion handoffs we've already consumed so re-renders
  // don't re-trigger the same generation.
  const consumedHandoffRef = useRef(null);

  useEffect(() => {
    saveSqlStudioRowLimit(rowLimit);
  }, [rowLimit]);

  useEffect(() => {
    setRowLimit((prev) => clampSqlStudioRowLimit(prev, modeId));
  }, [modeId]);

  // ── Oracle mode: AI SQL Runner BIP probe (cached) ─
  const runProbe = useCallback(
    async (forceRefresh = false) => {
      setProbing(true);
      try {
        const url = forceRefresh
          ? '/api/copilot/oracle-sql-runner/probe?refresh=1'
          : '/api/copilot/oracle-sql-runner/probe';
        const { data } = await API.get(url);
        setOracleProbe(data);
        if (connectionKey) saveOracleProbeCache(connectionKey, data);
      } catch (e) {
        setOracleProbe({
          installed: false,
          reportPathConfigured: false,
          bipProbeApiVersion: 6,
          error: {
            kind: 'unknown',
            message: e.response?.data?.error || e.message || 'Probe failed',
          },
        });
      } finally {
        setProbing(false);
      }
    },
    [connectionKey]
  );

  useEffect(() => {
    if (modeId !== 'oracle') return;
    const cached = connectionKey ? loadOracleProbeCache(connectionKey) : null;
    if (cached) {
      setOracleProbe(cached);
      return;
    }
    runProbe(false);
  }, [modeId, connectionKey, runProbe]);

  // ── Generate ─────────────────────────────────────────────────────────────
  const generate = async (q, options = {}) => {
    const trimmed = String(q || '').trim();
    const isClarificationFollowUp = Boolean(options.clarificationFollowUp);
    if (!trimmed || generating) return;

    if (isClarificationFollowUp) {
      if (!clarificationRound?.questions?.length) {
        setError('No pending clarifications.');
        return;
      }
    } else {
      setClarificationRound(null);
      setClarificationAnswers('');
    }

    setError(null);
    setGenerating(true);
    if (!isClarificationFollowUp) {
      setRunResult(null);
      setRefineMeta(null);
      setLearningOutcomeId(null);
      setUserVerdict(null);
      setLoadedSavedQuery(null);
    }

    try {
      const body = {
        question: trimmed,
        rowLimit: effectiveRowLimitForSql(modeId, editorSql, trimmed, rowLimit),
      };
      if (modeId === 'oracle' && isClarificationFollowUp && clarificationRound?.questions?.length) {
        body.clarificationContext = {
          questions: clarificationRound.questions,
          userAnswers: clarificationAnswers.trim(),
        };
      }

      const { data } = await API.post(mode.composeUrl, body);

      if (
        modeId === 'oracle' &&
        data.needsClarification &&
        Array.isArray(data.clarifyingQuestions) &&
        data.clarifyingQuestions.length > 0
      ) {
        setClarificationRound({
          questions: data.clarifyingQuestions,
          rationale: data.rationale || '',
          queryIntent: data.queryIntent || null,
        });
        setComposed({
          ok: false,
          needsClarification: true,
          rationale: data.rationale,
          sql: null,
          rowLimit: data.rowLimit,
          elapsedMs: data.elapsedMs,
        });
        setEditorSql('');
        return;
      }

      setClarificationRound(null);
      setClarificationAnswers('');
      setComposed({
        ...data,
        queryIntent: data.queryIntent || null,
      });
      setEditorSql(resolveComposedSql(data));
      if (modeId === 'oracle') {
        setLearningOutcomeId(data.learningOutcomeId || null);
        setLearningEnabled(data.learningEnabled !== false);
        setUserVerdict(null);
      }

      if (modeId === 'otbi') {
        // OTBI composer also runs the query in one shot, so we already have
        // rows. Reuse them as the initial runResult.
        if (data.ok) {
          setRunResult({
            ok: true,
            sql: data.sql,
            rows: data.rows || [],
            columns: data.columns || [],
            rowCount: data.rowCount,
            elapsedMs: data.elapsedMs,
          });
        } else {
          const partialSql = resolveComposedSql(data);
          setRunResult({
            ok: false,
            error: data.rationale || 'No working query could be composed.',
            sql: partialSql || data.sql,
            elapsedMs: data.elapsedMs,
          });
        }
      } else {
        // Oracle composer is generation-only. If the AI SQL Runner is
        // installed, auto-execute. Otherwise leave runResult null so the user
        // gets just the SQL + the setup card.
        if (data.ok && oracleProbe?.installed) {
          await runEditedSqlImpl(data.sql);
        }
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.error, e.message || 'Failed to compose SQL'));
    } finally {
      setGenerating(false);
    }
  };

  /** Oracle SQL: send current editor SQL + optional Preview error + user note back to the composer. */
  const reviseWithAi = async () => {
    if (modeId !== 'oracle' || generating) return;
    const sql = editorSql.trim();
    const note = revisionNote.trim();
    const errText =
      runResult && runResult.ok === false && runResult.error != null
        ? formatApiError(runResult.error, '').trim()
        : '';
    if (!sql) {
      setError('Add SQL in the editor before asking for a revision.');
      return;
    }
    if (!note && !errText) {
      setError(
        'Describe what to change, or run Preview first so the Oracle/BIP error text can be sent with your note.'
      );
      return;
    }
    setError(null);
    setGenerating(true);
    try {
      const { data } = await API.post(MODES.oracle.composeUrl, {
        question: question.trim() || '(Refining SQL — see revision payload)',
        rowLimit: effectiveRowLimitForSql(modeId, sql, question, rowLimit),
        revision: {
          currentSql: sql,
          userNote: note,
          errorMessage: errText,
        },
      });
      setComposed((prev) => ({
        ...(prev && typeof prev === 'object' ? prev : {}),
        ok: data.ok,
        sql: data.sql,
        rationale: data.rationale,
        rowLimit: data.rowLimit,
        elapsedMs: data.elapsedMs,
        revisionMeta: data.revisionMeta,
        schemaLint: data.schemaLint,
        patternIds: data.patternIds ?? prev?.patternIds,
      }));
      setEditorSql(data.sql || '');
      setLearningOutcomeId(data.learningOutcomeId || null);
      setLearningEnabled(data.learningEnabled !== false);
      setUserVerdict(null);
      if (data.revisionMeta && typeof data.revisionMeta === 'object') {
        setRefineMeta(data.revisionMeta);
      } else if (data.ok && data.sql) {
        setRefineMeta({
          sqlChanged: true,
          revisionSummary: 'Changes:\n(API did not return revisionMeta — see rationale above.)',
          hcmModelReview: [],
          serverNote: null,
        });
      } else {
        setRefineMeta(null);
      }
      if (data.ok && oracleProbe?.installed && data.sql) {
        await runEditedSqlImpl(data.sql);
      }
      setRevisionNote('');
    } catch (e) {
      setError(formatApiError(e.response?.data?.error, e.message || 'Revision failed'));
      setRefineMeta(null);
    } finally {
      setGenerating(false);
    }
  };

  // Auto-run when handed an initialQuestion from another view (e.g. the
  // "Generate SQL" icon on AskDataPanel). We key off `initialQuestion.ts` so a
  // re-handoff with the same text still re-runs.
  useEffect(() => {
    if (!initialQuestion?.question) return;
    if (consumedHandoffRef.current === initialQuestion.ts) return;
    consumedHandoffRef.current = initialQuestion.ts;
    setQuestion(initialQuestion.question);
    generate(initialQuestion.question);
    if (typeof onConsumeInitial === 'function') onConsumeInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuestion]);

  // ── Run / re-run edited SQL ──────────────────────────────────────────────
  //
  // Split into two functions so the composer can re-use the same execution
  // path internally without juggling the editor state.
  const runEditedSqlImpl = async (sqlOverride) => {
    const sql = (sqlOverride ?? editorSql).trim();
    if (!sql || executing) return;
    setError(null);
    setExecuting(true);
    try {
      const body = {
        sql,
        rowLimit: effectiveRowLimitForSql(modeId, sql, question, rowLimit),
      };
      if (modeId === 'oracle') {
        Object.assign(
          body,
          deriveOracleRunHdlFlags({ sql, question, composed })
        );
      }
      if (modeId === 'oracle' && learningOutcomeId) {
        body.learningOutcomeId = learningOutcomeId;
      }
      const { data } = await API.post(mode.runUrl, body, { validateStatus: () => true });
      const normalized = modeId === 'oracle' ? normalizeOracleRunPayload(data) : data;
      setRunResult(normalized);
      if (
        modeId === 'oracle' &&
        normalized?.ok === false &&
        (normalized.errorKind === 'not_installed' || normalized.errorKind === 'permission')
      ) {
        runProbe(true);
      }
    } catch (e) {
      const payload = e.response?.data;
      if (modeId === 'oracle' && payload && typeof payload === 'object' && payload.ok === false) {
        setRunResult(normalizeOracleRunPayload(payload));
      } else {
        setError(formatApiError(payload?.error, e.message || 'Failed to run SQL'));
      }
    } finally {
      setExecuting(false);
    }
  };
  const runEditedSql = () => runEditedSqlImpl();

  const exportOracleBip = useCallback(
    async (format) => {
      const sql = (editorSql || '').trim();
      if (!sql || exportingFormat) return;
      setExportingFormat(format);
      setError(null);
      try {
        const res = await API.post(
          '/api/copilot/run-oracle-sql/export',
          {
            sql,
            rowLimit,
            format,
            ...(modeId === 'oracle'
              ? deriveOracleRunHdlFlags({ sql, question, composed })
              : {}),
          },
          { responseType: 'blob', validateStatus: () => true }
        );
        const blob = res.data;
        const ct = String(res.headers['content-type'] || '');
        if (res.status !== 200) {
          let msg = `Export failed (${res.status})`;
          try {
            const text = await blob.text();
            try {
              const j = JSON.parse(text);
              msg = j.error || j.message || msg;
            } catch {
              if (text) msg = text.slice(0, 600);
            }
          } catch {
            /* ignore */
          }
          setError(msg);
          return;
        }
        if (ct.includes('application/json')) {
          try {
            const text = await blob.text();
            const j = JSON.parse(text);
            setError(j.error || j.message || 'Export failed');
          } catch {
            setError('Export failed');
          }
          return;
        }
        const ext =
          format === 'xlsx'
            ? 'xlsx'
            : format === 'pdf'
              ? 'pdf'
              : format === 'xml'
                ? 'xml'
                : 'csv';
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sql-studio-${Date.now()}.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        setError(e.message || 'Export failed');
      } finally {
        setExportingFormat(null);
      }
    },
    [editorSql, rowLimit, exportingFormat]
  );

  const copySql = async () => {
    if (!editorSql) return;
    try {
      await navigator.clipboard.writeText(editorSql);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      /* clipboard may be disabled in some browsers; silently no-op */
    }
  };

  /** Save current editor SQL — updates loaded saved query, or creates a new personal copy. */
  const saveToLibrary = async () => {
    const sql = editorSql.trim();
    if (!sql || saveBusy) return;

    let createName = null;
    if (!loadedSavedQuery?.id) {
      const defaultName = formatDefaultQueryName(
        question.trim() || composed?.queryIntent?.title || ''
      );
      const entered = window.prompt('Save to your query library as:', defaultName);
      if (entered === null) return;
      createName = entered.trim();
      if (!createName) {
        setError('Enter a name to save this query.');
        return;
      }
    }

    setSaveBusy(true);
    setError(null);
    try {
      if (loadedSavedQuery?.id) {
        const name = loadedSavedQuery.name || formatDefaultQueryName();
        const updated = await updateSqlQuery(loadedSavedQuery.id, { name, sql, mode: modeId });
        if (updated?.id) {
          setLoadedSavedQuery({ id: updated.id, name: updated.name || name });
        }
      } else {
        const created = await saveSqlQuery({ name: createName, sql, mode: modeId });
        if (created?.id) {
          setLoadedSavedQuery({ id: created.id, name: created.name || createName });
        }
      }
      setSavedToLibrary(true);
      setSavedQueriesRefreshKey((k) => k + 1);
      setTimeout(() => setSavedToLibrary(false), 2500);
    } catch (e) {
      setError(e.message || 'Could not save query to library');
    } finally {
      setSaveBusy(false);
    }
  };

  /** Preview table, export state, and run/verdict errors — keeps question and SQL editor. */
  const clearResults = () => {
    setRunResult(null);
    setExportingFormat(null);
    setUserVerdict(null);
    setError(null);
  };

  const reset = () => {
    setQuestion('');
    setComposed(null);
    setEditorSql('');
    clearResults();
    setRevisionNote('');
    setClarificationRound(null);
    setClarificationAnswers('');
    setRefineMeta(null);
    setLearningOutcomeId(null);
    setLoadedSavedQuery(null);
  };

  const submitSqlVerdict = async (positive) => {
    if (modeId !== 'oracle' || verdictBusy || userVerdict) return;
    if (!runResult) {
      setError('Run Preview before rating this SQL.');
      return;
    }
    const finalSql = editorSql.trim();
    const baseSql = String(composed?.sql || '').trim();
    const verdict =
      positive && finalSql && baseSql && finalSql !== baseSql ? 'edited' : positive ? 'accepted' : 'rejected';
    setVerdictBusy(true);
    setError(null);
    try {
      const feedbackBody = {
        verdict,
        finalSql,
        learningOutcomeId,
        question: question.trim(),
        patternIds: composed?.patternIds || [],
        generatedSql: composed?.sql || '',
        previewOk: !!runResult.ok,
        rowCount: runResult.rowCount,
        elapsedMs: runResult.elapsedMs,
        previewError: runResult.ok
          ? null
          : typeof runResult.error === 'string'
            ? runResult.error
            : formatApiError(runResult.error, null),
        interactionId: composed?.interactionId || null,
        revised: !!composed?.revisionMeta,
      };
      let data;
      try {
        ({ data } = await API.post('/api/copilot/sql-outcomes/feedback', feedbackBody));
      } catch (e) {
        if (e.response?.status === 404 && learningOutcomeId) {
          ({ data } = await API.post(
            `/api/copilot/sql-outcomes/${learningOutcomeId}/verdict`,
            { verdict, finalSql }
          ));
        } else if (e.response?.status === 404) {
          throw new Error(
            'SQL feedback API not found. Restart the API from the latest code: stop node server.js, then run it again on the same PORT as client/.env REACT_APP_PROXY_TARGET (usually 3010).'
          );
        } else {
          throw e;
        }
      }
      if (data?.learningOutcomeId) setLearningOutcomeId(data.learningOutcomeId);
      if (data?.success) setUserVerdict(data.userVerdict || verdict);
    } catch (e) {
      setError(formatApiError(e.response?.data?.error, e.message || 'Failed to save feedback'));
    } finally {
      setVerdictBusy(false);
    }
  };

  const switchMode = (newId) => {
    if (newId === modeId) return;
    setModeId(newId);
    // Clear results when switching modes so we don't show OTBI output under
    // an Oracle composition (or vice versa).
    reset();
  };

  const rows = runResult?.rows || [];
  const cols = runResult?.columns || [];
  const hasRows = runResult?.ok && rows.length > 0;
  const bipExportReady = oracleRunnerPathConfigured(oracleProbe);
  const hasClearableResults = !!(runResult || exportingFormat || userVerdict);

  useEffect(() => {
    if (modeId !== 'oracle') return;
    if (sqlLooksLikeSecurityAccessMap(editorSql) || questionLooksLikeSecurityAccessMap(question)) {
      setRowLimit((prev) => Math.max(prev, SECURITY_ACCESS_MAP_ROW_LIMIT));
    }
  }, [modeId, editorSql, question]);

  // In Oracle mode, the Run button is disabled until the AI SQL Runner is
  // confirmed installed. We're permissive (allow execution attempts if the
  // probe hasn't returned yet) but explicit (clearly tell the user when we
  // know it isn't installed).
  const runDisabledByMode =
    modeId === 'oracle' && oracleProbe?.error?.kind === 'not_configured';
  const hcmReady = hcmConnection?.isConfigured === true;

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-6 py-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Terminal className="h-5 w-5 text-indigo-600" />
            <h1 className="text-lg font-semibold text-slate-900">{title}</h1>
          </div>

          {/* Mode toggle */}
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200 bg-slate-100 text-xs">
            {Object.values(MODES).map((m) => {
              const active = m.id === modeId;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => switchMode(m.id)}
                  className={
                    active
                      ? 'bg-white px-3 py-1.5 font-medium text-indigo-700 shadow-sm'
                      : 'px-3 py-1.5 text-slate-600 hover:text-slate-900'
                  }
                  title={m.description}
                >
                  {m.label}
                </button>
              );
            })}
          </div>

          {/* Oracle-mode install indicator */}
          {modeId === 'oracle' && (
            <span
              className={
                oracleProbe?.installed
                  ? 'inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700'
                  : oracleProbe
                  ? 'inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700'
                  : 'inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-500'
              }
              title={
                oracleProbe?.reportPath
                  ? `${oracleProbe.reportPath}${
                      oracleProbe.reportPathSource
                        ? ` · ${formatReportPathSource(oracleProbe.reportPathSource)}`
                        : ''
                    }`
                  : ''
              }
            >
              {probing && <Loader2 className="h-3 w-3 animate-spin" />}
              {!probing && oracleProbe?.installed && <Check className="h-3 w-3" />}
              {!probing && oracleProbe && !oracleProbe.installed && <AlertCircle className="h-3 w-3" />}
              {probing
                ? 'Probing BIP…'
                : oracleProbe?.installed
                ? `BIP runner: ${oracleProbe.reportPathSource === 'saas_tool_default' ? 'SAAS Tool' : 'installed'}`
                : oracleProbe
                ? oracleProbe?.error?.kind === 'not_configured'
                  ? 'BIP SQL runner: path not set'
                  : oracleProbe?.error?.kind === 'permission'
                  ? 'BIP SQL runner: access denied'
                  : 'BIP SQL runner: not installed'
                : 'BIP SQL runner: unknown'}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-5">
        {!hcmReady && (
          <div className="mb-4">
            <ConnectionRequiredNotice onOpenConnections={onOpenConnections} />
          </div>
        )}
        {modeId === 'oracle' && (
          <SqlStudioToolbar
            modeId={modeId}
            editorSql={editorSql}
            setEditorSql={setEditorSql}
            onRecipeLoaded={() => setEditorExpandSignal((n) => n + 1)}
            question={question}
            composed={composed}
            onOpenUserLookup={onOpenUserLookup}
            runResult={runResult}
            executing={executing}
            savedQueriesRefreshKey={savedQueriesRefreshKey}
            loadedSavedQuery={loadedSavedQuery}
            onLoadedSavedQueryChange={setLoadedSavedQuery}
            onSavedQueriesChanged={() => setSavedQueriesRefreshKey((k) => k + 1)}
          />
        )}
        {/* Question input */}
        <CollapsibleStrip
          storageKey="aiTool.sqlStudio.compose"
          title="Question"
          collapsedSummary={
            question.trim()
              ? question.length > 72
                ? `${question.slice(0, 72)}…`
                : question
              : 'Natural language → SQL'
          }
        >
          <div className="p-4">
          <QueryHintChips
            onPick={(q) => {
              setQuestion(q);
              if (modeId === 'oracle') generate(q);
            }}
            className="mb-3"
          />
          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-stretch">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  generate(question);
                }
              }}
              placeholder={
                modeId === 'otbi'
                  ? 'Describe the report you need…'
                  : 'Describe the query you need…'
              }
              className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              disabled={generating}
            />
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700">
                Max rows:
                <input
                  type="number"
                  min={1}
                  max={modeId === 'otbi' ? SQL_STUDIO_OTBI_ROW_LIMIT_MAX : SQL_STUDIO_ORACLE_ROW_LIMIT_MAX}
                  step={1}
                  value={rowLimit}
                  onChange={(e) =>
                    setRowLimit(clampSqlStudioRowLimit(e.target.value, modeId))
                  }
                  className="w-[5.25rem] rounded-md border border-slate-300 px-2 py-1.5 text-xs tabular-nums focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  title={`Rows returned when previewing or running (1–${
                    modeId === 'otbi' ? SQL_STUDIO_OTBI_ROW_LIMIT_MAX : SQL_STUDIO_ORACLE_ROW_LIMIT_MAX
                  })`}
                />
              </label>
              <button
                type="button"
                onClick={() => generate(question)}
                disabled={generating || !question.trim()}
                className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {generating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                Generate SQL
              </button>
              {(composed || runResult) && (
                <button
                  type="button"
                  onClick={reset}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-2 text-xs text-slate-700 hover:bg-slate-50"
                  title="Clear question, generated SQL, and preview results"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Clear all
                </button>
              )}
            </div>
          </div>

          {(!composed || composed?.needsClarification) && !generating && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {mode.starters.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    setQuestion(s);
                    generate(s);
                  }}
                  className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] text-slate-600 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {error && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {modeId === 'oracle' && clarificationRound && (
            <div className="mt-3 rounded-lg border border-sky-300 bg-sky-50 p-3 text-sm text-slate-800 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-sky-800">Clarifications needed</div>
              {clarificationRound.rationale ? (
                <p className="mt-1 text-xs text-slate-600">{clarificationRound.rationale}</p>
              ) : null}
              <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-xs text-slate-800">
                {clarificationRound.questions.map((cq, i) => (
                  <li key={i}>{cq}</li>
                ))}
              </ol>
              <label className="mt-3 block text-[11px] font-medium text-slate-600">Your answers</label>
              <textarea
                value={clarificationAnswers}
                onChange={(e) => setClarificationAnswers(e.target.value)}
                rows={4}
                spellCheck={false}
                disabled={generating}
                placeholder="Short answers to each question above"
                className="mt-1 w-full resize-y rounded-md border border-sky-200 bg-white p-2 font-mono text-xs text-slate-800 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-400 disabled:opacity-60"
              />
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => generate(question.trim(), { clarificationFollowUp: true })}
                  disabled={generating || !question.trim()}
                  className="inline-flex items-center gap-1.5 rounded-md bg-sky-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  Continue with answers
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setClarificationRound(null);
                    setClarificationAnswers('');
                    setComposed(null);
                    setEditorSql('');
                  }}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
          </div>
        </CollapsibleStrip>

        {/* Setup card in Oracle mode when runner isn't installed */}
        {modeId === 'oracle' && oracleProbe && !oracleProbe.installed && (
          <AiSqlRunnerSetupCard
            probe={oracleProbe}
            onRetryProbe={() => runProbe(true)}
            retrying={probing}
          />
        )}

        {/* Composer metadata — OTBI-only (Oracle has no subject area) */}
        {modeId === 'otbi' && composed && (
          <div className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50/40 p-4">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-full bg-indigo-100 px-2 py-0.5 font-medium text-indigo-700">
                Subject area
              </span>
              <span className="font-mono text-indigo-900">{composed.saName || '—'}</span>
              {composed.elapsedMs != null && (
                <span className="ml-auto flex items-center gap-1 text-indigo-600">
                  <Clock className="h-3 w-3" />
                  {(composed.elapsedMs / 1000).toFixed(1)}s
                </span>
              )}
            </div>
            {composed.saRationale && (
              <p className="mt-1 text-[11px] italic text-indigo-700/80">
                Why this SA: {composed.saRationale}
              </p>
            )}
            {composed.rationale && (
              <p className="mt-1 text-[11px] italic text-slate-600">
                Query intent: {composed.rationale}
              </p>
            )}
            {composed.attempts?.length > 1 && (
              <p className="mt-1 text-[11px] text-amber-700">
                Took {composed.attempts.length} attempts (self-healed after Oracle errors).
              </p>
            )}
          </div>
        )}

        {/* SQL editor */}
        {(((composed && !composed.needsClarification) || editorSql)) && (
          <CollapsibleStrip
            storageKey="aiTool.sqlStudio.editor"
            expandSignal={editorExpandSignal}
            className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
            title={`Generated ${mode.sqlLanguageLabel}`}
            titleClassName="text-xs font-semibold uppercase tracking-wide text-gray-900"
            subtitle="Editable — tweak and re-run"
            subtitleClassName="text-[11px] text-gray-700"
            collapsedSummary={
              editorSql.trim()
                ? `${editorSql.replace(/\s+/g, ' ').trim().slice(0, 72)}${editorSql.length > 72 ? '…' : ''}`
                : 'No SQL yet'
            }
            headerActions={
              <>
                {composed?.elapsedMs != null && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                    <Clock className="h-3 w-3" />
                    {(composed.elapsedMs / 1000).toFixed(1)}s
                  </span>
                )}
                <button
                  type="button"
                  onClick={copySql}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
                  disabled={!editorSql}
                >
                  {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
                <button
                  type="button"
                  onClick={saveToLibrary}
                  title={
                    loadedSavedQuery?.id
                      ? `Update saved query “${loadedSavedQuery.name}”`
                      : 'Save edited SQL to your personal query library'
                  }
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={!editorSql.trim() || saveBusy}
                >
                  {saveBusy ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : savedToLibrary ? (
                    <Check className="h-3 w-3 text-emerald-600" />
                  ) : (
                    <Save className="h-3 w-3" />
                  )}
                  {saveBusy
                    ? 'Saving…'
                    : savedToLibrary
                      ? loadedSavedQuery?.id
                        ? 'Updated'
                        : 'Saved'
                      : loadedSavedQuery?.id
                        ? 'Update'
                        : 'Save'}
                </button>
                <label className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700">
                  Max rows:
                  <input
                    type="number"
                    min={1}
                    max={modeId === 'otbi' ? SQL_STUDIO_OTBI_ROW_LIMIT_MAX : SQL_STUDIO_ORACLE_ROW_LIMIT_MAX}
                    step={1}
                    value={rowLimit}
                    onChange={(e) =>
                      setRowLimit(clampSqlStudioRowLimit(e.target.value, modeId))
                    }
                    className="w-[5.25rem] rounded border border-slate-300 px-1.5 py-0.5 text-xs tabular-nums focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    title={`Rows returned when previewing or running (1–${
                      modeId === 'otbi' ? SQL_STUDIO_OTBI_ROW_LIMIT_MAX : SQL_STUDIO_ORACLE_ROW_LIMIT_MAX
                    })`}
                  />
                </label>
                <button
                  type="button"
                  onClick={runEditedSql}
                  disabled={executing || !editorSql.trim() || runDisabledByMode}
                  title={
                    runDisabledByMode
                      ? 'Install or fix the BIP SQL runner report (see card above)'
                      : modeId === 'oracle'
                        ? 'Load a row preview from BIP; use Excel / CSV / PDF above the grid to download.'
                        : 'Run this SQL'
                  }
                  className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {executing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                  {modeId === 'oracle' ? 'Preview' : 'Run'}
                </button>
              </>
            }
          >
            <textarea
              value={editorSql}
              onChange={(e) => setEditorSql(e.target.value)}
              spellCheck={false}
              className="block h-44 w-full resize-y border-0 bg-white p-3 font-mono text-xs font-medium leading-relaxed text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-200"
              placeholder="(generated SQL will appear here — you can edit it before running)"
            />
            {modeId === 'oracle' && (
              <div className="rounded-b-xl border-t border-slate-200 bg-slate-50 px-3 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-900">
                    Refine with AI
                  </label>
                  {runResult && runResult.ok === false && runResult.error && (
                    <span className="text-[10px] font-medium text-amber-800">
                      Last Preview error will be included automatically
                    </span>
                  )}
                </div>
                <textarea
                  value={revisionNote}
                  onChange={(e) => setRevisionNote(e.target.value)}
                  spellCheck={false}
                  disabled={generating}
                  rows={3}
                  placeholder="e.g. “ORA-00904 on EFFECTIVE_END_DATE — use PER_ASSIGNMENT_STATUS_TYPES without _VL” or “add department name from HR_ALL_ORGANIZATION_UNITS_F”"
                  className="mt-2 w-full resize-y rounded-md border border-slate-300 bg-white p-2 font-mono text-[11px] font-medium leading-relaxed text-gray-900 placeholder:text-slate-500 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500 disabled:opacity-60"
                />
                <button
                  type="button"
                  onClick={reviseWithAi}
                  disabled={
                    generating ||
                    !editorSql.trim() ||
                    (!revisionNote.trim() &&
                      !(runResult && runResult.ok === false && String(runResult.error || '').trim()))
                  }
                  className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-[11px] font-medium text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {generating ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5" />
                  )}
                  Revise SQL with AI
                </button>
                {refineMeta && (
                  <div
                    className={`mt-3 rounded-lg border px-3 py-2.5 text-[11px] leading-snug ${
                      refineMeta.sqlChanged
                        ? 'border-violet-400/50 bg-violet-950/80 text-violet-100'
                        : 'border-slate-500/50 bg-slate-900/90 text-slate-200'
                    }`}
                  >
                    <div className="font-semibold text-slate-100">
                      {refineMeta.sqlChanged ? 'Refinement result — SQL updated' : 'Refinement result — no substantive SQL change'}
                    </div>
                    {refineMeta.revisionSummary ? (
                      <pre className="mt-1.5 max-h-40 overflow-y-auto whitespace-pre-wrap font-sans text-[11px] text-slate-100/95">
                        {refineMeta.revisionSummary}
                      </pre>
                    ) : null}
                    {refineMeta.serverNote ? (
                      <p className="mt-2 rounded border border-amber-500/40 bg-amber-950/40 px-2 py-1 text-[10px] text-amber-100">
                        {refineMeta.serverNote}
                      </p>
                    ) : null}
                    {Array.isArray(refineMeta.hcmModelReview) && refineMeta.hcmModelReview.length > 0 ? (
                      <div className="mt-2 border-t border-white/10 pt-2">
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                          HCM model review
                        </div>
                        <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[10px] text-slate-300">
                          {refineMeta.hcmModelReview.map((line, i) => (
                            <li key={i}>{line}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            )}
          </CollapsibleStrip>
        )}

        {/* Run result */}
        {runResult && (
          <CollapsibleStrip
            storageKey="aiTool.sqlStudio.preview"
            className="mt-4 rounded-xl border border-slate-200 bg-white shadow-sm"
            title="Preview results"
            collapsedSummary={
              runResult.ok
                ? `${fmtCount(runResult.rowCount ?? rows.length)} rows${
                    runResult.elapsedMs != null ? ` · ${(runResult.elapsedMs / 1000).toFixed(1)}s` : ''
                  }`
                : runResult.errorKind === 'oracle_error'
                  ? 'Oracle error'
                  : 'Run failed'
            }
            headerActions={
              <>
              {runResult.ok ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                  <Check className="h-3 w-3" />
                  {fmtCount(runResult.rowCount ?? rows.length)} rows
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700">
                  <AlertCircle className="h-3 w-3" />
                  {runResult.errorKind === 'oracle_error'
                    ? 'Oracle error'
                    : runResult.errorKind === 'validation'
                      ? 'Validation'
                      : 'Error'}
                </span>
              )}
              {runResult.elapsedMs != null && (
                <span className="flex items-center gap-1 text-xs text-slate-400">
                  <Clock className="h-3 w-3" />
                  {(runResult.elapsedMs / 1000).toFixed(1)}s
                </span>
              )}
              <button
                type="button"
                onClick={clearResults}
                disabled={!hasClearableResults}
                className="ml-auto inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                title="Clear preview table, export state, and run errors (keeps SQL in editor)"
              >
                Clear results
              </button>
              {hasRows && modeId === 'otbi' && (
                <button
                  type="button"
                  onClick={() =>
                    downloadCsv(
                      `sql-studio-${Date.now()}.csv`,
                      (cols || []).map((c) => (typeof c === 'string' ? c : c.name)),
                      rows
                    )
                  }
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
                >
                  <Download className="h-3 w-3" />
                  CSV
                </button>
              )}
              </>
            }
          >
            {modeId === 'oracle' && (
              <OracleSqlFeedbackBar
                className="mx-4 mb-0 mt-3"
                needsPreview={false}
                learningEnabled={learningEnabled}
                userVerdict={userVerdict}
                verdictBusy={verdictBusy}
                onPositive={() => submitSqlVerdict(true)}
                onNegative={() => submitSqlVerdict(false)}
              />
            )}

            <div className="p-4">
              {runResult.ok ? (
                hasRows ? (
                  <>

                    {modeId === 'oracle' && (
                      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            title={bipExportReady ? BIP_EXPORT_CSV_TOOLTIP : BIP_EXPORT_TOOLTIP}
                            disabled={!!exportingFormat || !bipExportReady}
                            onClick={() => exportOracleBip('csv')}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-violet-700 hover:bg-violet-50 disabled:pointer-events-none disabled:opacity-50"
                          >
                            {exportingFormat === 'csv' ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            type="button"
                            title={bipExportReady ? BIP_EXPORT_XLSX_TOOLTIP : BIP_EXPORT_TOOLTIP}
                            disabled={!!exportingFormat || !bipExportReady}
                            onClick={() => exportOracleBip('xlsx')}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-emerald-700 hover:bg-emerald-50 disabled:pointer-events-none disabled:opacity-50"
                          >
                            {exportingFormat === 'xlsx' ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <FileSpreadsheet className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            type="button"
                            title={bipExportReady ? BIP_EXPORT_PDF_TOOLTIP : BIP_EXPORT_TOOLTIP}
                            disabled={!!exportingFormat || !bipExportReady}
                            onClick={() => exportOracleBip('pdf')}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-rose-700 hover:bg-rose-50 disabled:pointer-events-none disabled:opacity-50"
                          >
                            {exportingFormat === 'pdf' ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <FileText className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="mb-1.5 flex items-center justify-between text-[11px] text-slate-500">
                      <span>
                        {(Array.isArray(cols) ? cols.length : 0).toLocaleString()} columns ×{' '}
                        {fmtCount(rows.length)} rows
                      </span>
                      {Array.isArray(cols) && cols.length > 5 && (
                        <span className="text-slate-400">Scroll right to see all columns →</span>
                      )}
                    </div>
                    <ResultTable columns={cols} rows={rows} />
                  </>
                ) : (
                  <div
                    className={`rounded-md px-3 py-4 text-center text-xs ${
                      modeId === 'oracle' && bipRestReportNotFound(runResult.runner, runResult.errorKind)
                        ? 'border border-amber-200 bg-amber-50 text-amber-950'
                        : 'border border-slate-200 bg-slate-50 text-slate-500'
                    }`}
                  >
                    {modeId === 'oracle' && bipRestReportNotFound(runResult.runner, runResult.errorKind) ? (
                      <>
                        <p className="font-semibold text-amber-900">
                          BIP report not found at{' '}
                          <code className="rounded bg-amber-100 px-1 font-mono text-[11px]">
                            {bipNotFoundPathLabel(runResult.runner)}
                          </code>
                        </p>
                        <p className="mx-auto mt-2 max-w-xl text-left text-[11px] text-amber-800/95">
                          BIP returned 404 for this runner path. Set{' '}
                          <code className="rounded bg-amber-100 px-0.5 font-mono">AI_SQL_RUNNER_REPORT_PATH</code> or{' '}
                          <code className="font-mono">aiSqlRunnerReportPath</code>, then re-check installation.
                        </p>
                      </>
                    ) : (
                      <p>Query ran but returned no rows.</p>
                    )}
                    {modeId === 'oracle' &&
                      Array.isArray(runResult.runner?.zeroRowHints) &&
                      runResult.runner.zeroRowHints.length > 0 && (
                        <ul className="mx-auto mt-2 max-w-xl list-inside list-disc text-left text-[11px] text-slate-600">
                          {runResult.runner.zeroRowHints.map((h, i) => (
                            <li key={i} className="mt-1">
                              {h}
                            </li>
                          ))}
                        </ul>
                      )}
                    {modeId === 'oracle' && (
                      <details className="mt-3 rounded-md border border-slate-200 bg-white px-3 py-2 text-left">
                        <summary className="cursor-pointer text-[11px] font-semibold text-slate-700 hover:text-slate-900">
                          Technical details (for support)
                        </summary>
                        <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap rounded bg-slate-50 p-2 font-mono text-[10px] text-slate-700">
                          {JSON.stringify(
                            runResult.runner != null
                              ? runResult.runner
                              : {
                                  _notice:
                                    'No `runner` on this response — almost always wrong API port/origin. Fix `client/.env` REACT_APP_PROXY_TARGET to match root `.env` PORT, restart `npm start`, restart `node server.js`.',
                                  responseKeys: Object.keys(runResult),
                                  responseHasApiBuildV2: runResult._apiBuild === 'run-oracle-sql-v2',
                                  rowCount: runResult.rowCount,
                                  columnCount: Array.isArray(runResult.columns) ? runResult.columns.length : 0,
                                  clientApiBase: getApiBase() || '(empty → CRA same-origin proxy)',
                                  reactAppProxyTarget:
                                    typeof process !== 'undefined' && process.env.REACT_APP_PROXY_TARGET
                                      ? process.env.REACT_APP_PROXY_TARGET
                                      : '(unset → setupProxy defaults to http://localhost:3002)',
                                  reactAppApiUrl:
                                    typeof process !== 'undefined' && process.env.REACT_APP_API_URL
                                      ? process.env.REACT_APP_API_URL
                                      : '(unset)',
                                },
                            null,
                            2
                          )}
                        </pre>
                      </details>
                    )}
                  </div>
                )
              ) : (
                <pre className="overflow-auto rounded-md bg-rose-50 p-3 font-mono text-[11px] leading-relaxed text-rose-800">
                  {typeof runResult.error === 'string'
                    ? runResult.error
                    : runResult.error?.message || `${mode.short} rejected the query.`}
                </pre>
              )}
            </div>
          </CollapsibleStrip>
        )}
      </div>
    </div>
  );
}
